/**
 * pH, pOH, [H⁺] and [OH⁻] at 25 °C.
 *
 *     pH = −log₁₀[H⁺]     pOH = −log₁₀[OH⁻]
 *     Kw = [H⁺][OH⁻] = 1.0 × 10⁻¹⁴     pH + pOH = 14.00
 *
 * Strong and weak solutions are solved exactly from the charge balance
 * (`acid-base.ts`), including water's own ionisation, and the textbook quadratic and
 * small-x approximation are reported alongside so the 5 % rule can be seen at work.
 */

import {
  chargedFraction,
  fromP,
  KW,
  PKW,
  pFrom,
  phOfMixture,
  type WeakEquilibrium,
  weakEquilibrium,
} from "@/lib/tool-kit/chem-reactions/acid-base";
import { parseSigFigNumber } from "@/lib/tool-kit/math/sig-figs";

export interface PhValues {
  pH: number;
  pOH: number;
  /** mol/L */
  hydrogen: number;
  /** mol/L */
  hydroxide: number;
}

export type KnownKind = "pH" | "pOH" | "hydrogen" | "hydroxide";

export function valuesFromPh(pH: number): PhValues {
  return {
    pH,
    pOH: PKW - pH,
    hydrogen: fromP(pH),
    hydroxide: fromP(PKW - pH),
  };
}

/** All four quantities from whichever one is known; null when it is not usable. */
export function valuesFromKnown(
  kind: KnownKind,
  value: number
): PhValues | null {
  if (!Number.isFinite(value)) {
    return null;
  }
  switch (kind) {
    case "pH":
      return valuesFromPh(value);
    case "pOH":
      return valuesFromPh(PKW - value);
    case "hydrogen":
      return value > 0 ? valuesFromPh(pFrom(value)) : null;
    default:
      return value > 0 ? valuesFromPh(PKW - pFrom(value)) : null;
  }
}

export type Reaction = "acidic" | "neutral" | "basic";

const NEUTRAL_TOLERANCE = 1e-9;

/** At 25 °C: acidic below pH 7, basic above. */
export function classify(pH: number): Reaction {
  if (Math.abs(pH - PKW / 2) <= NEUTRAL_TOLERANCE) {
    return "neutral";
  }
  return pH < PKW / 2 ? "acidic" : "basic";
}

/** Digits after the decimal point in a typed number: 3.60 → 2; null when unreadable. */
export function decimalsOf(raw: string): number | null {
  const parsed = parseSigFigNumber(raw);
  return "error" in parsed ? null : Math.max(parsed.decimalPlaces, 0);
}

export type SoluteSide = "acid" | "base";

export interface StrongSolution {
  values: PhValues;
  /** What pH would be if water's own ionisation were ignored: −log of the ion concentration. */
  ignoringWater: number;
  /** The ion concentration n × C that the strong solute supplies, mol/L. */
  ionConcentration: number;
  /** Water's ions are not negligible beside the solute's. */
  waterMatters: boolean;
}

const WATER_MATTERS_BELOW = 1e-6;

/** A fully dissociated acid or base giving `ions` H⁺ or OH⁻ per formula unit. */
export function strongSolution(
  side: SoluteSide,
  concentration: number,
  ions: number
): StrongSolution {
  const ionConcentration = concentration * ions;
  const pH = phOfMixture({
    solute: { kind: side === "acid" ? "strong-acid" : "strong-base" },
    concentration: ionConcentration,
    titrantIon: 0,
  });
  return {
    values: valuesFromPh(pH),
    ignoringWater:
      side === "acid" ? pFrom(ionConcentration) : PKW - pFrom(ionConcentration),
    ionConcentration,
    waterMatters: ionConcentration < WATER_MATTERS_BELOW,
  };
}

export interface WeakSolution {
  values: PhValues;
  /** [H⁺] for an acid, [OH⁻] for a base, from the exact charge balance. */
  ionExact: number;
  /** pH from the textbook quadratic (water ignored). */
  quadraticPh: number;
  /** pH from x ≈ √(K·C). */
  approximatePh: number;
  textbook: WeakEquilibrium;
  /** Percent of the solute that has reacted, from the exact solution. */
  percentIonised: number;
}

function phFromIon(side: SoluteSide, ion: number): number {
  return side === "acid" ? pFrom(ion) : PKW - pFrom(ion);
}

/** A weak acid (Ka) or weak base (Kb) of concentration C. */
export function weakSolution(
  side: SoluteSide,
  k: number,
  concentration: number
): WeakSolution {
  const solute = {
    kind: side === "acid" ? ("weak-acid" as const) : ("weak-base" as const),
    k,
  };
  const pH = phOfMixture({ solute, concentration, titrantIon: 0 });
  const values = valuesFromPh(pH);
  const textbook = weakEquilibrium(k, concentration);
  return {
    values,
    ionExact: side === "acid" ? values.hydrogen : values.hydroxide,
    quadraticPh: phFromIon(side, textbook.quadratic),
    approximatePh: phFromIon(side, textbook.approximate),
    textbook,
    percentIonised: chargedFraction(solute, values.hydrogen) * 100,
  };
}

/** Ka from Kb of the conjugate pair, or the reverse: Ka × Kb = Kw. */
export function conjugateConstant(k: number): number {
  return KW / k;
}
